import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ModalMC from '../../../components/ModalMC';
import ButtonMC from '../../../components/ButtonMC';
import IconMC from '../../../components/IconMC';
import WhatsappPhoneMockupMC from './WhatsappPhoneMockupMC';
import WhatsappFlowModelMC from '../../../models/WhatsappFlowModelMC';
import { getWhatsappFlowsConnectionRequirement } from '../utils/whatsappFlowsSyncUtils';

const STEPS = ['what_is', 'how_it_works', 'use_cases', 'requirements'];
const META_FLOWS_DOCS_URL = 'https://developers.facebook.com/docs/whatsapp/flows';

const FLOW_STEPS = [
  { key: 'create', icon: 'edit-outline' },
  { key: 'sync', icon: 'sync-outline' },
  { key: 'launch', icon: 'message-circle-outline' }
];

const USE_CASES = [
  { key: 'lead_qualification', icon: 'person-add-outline', iconClassName: 'tw-text-[#1877f2]' },
  { key: 'order_recovery', icon: 'credit-card-outline', iconClassName: 'tw-text-[#25d366]' },
  { key: 'bookings', icon: 'calendar-outline', iconClassName: 'tw-text-[#4B89BF]' },
  { key: 'satisfaction_survey', icon: 'star-outline', iconClassName: 'tw-text-[#f59e0b]' },
  { key: 'event_registration', icon: 'people-outline', iconClassName: 'tw-text-[#8b5cf6]' },
  { key: 'support_ticket', icon: 'question-mark-circle-outline', iconClassName: 'tw-text-[#ef4444]' }
];

const USE_CASE_FIELD_KEYS = {
  lead_qualification: [
    { labelKey: 'name_label', valueKey: 'name_value' },
    { labelKey: 'budget_label', valueKey: 'budget_value', isSelect: true },
    { labelKey: 'city_label', valueKey: 'city_value' },
    { labelKey: 'product_label', valueKey: 'product_value', isSelect: true }
  ],
  order_recovery: [
    { labelKey: 'order_label', valueKey: 'order_value' },
    { labelKey: 'address_label', valueKey: 'address_value' },
    { labelKey: 'payment_label', valueKey: 'payment_value', isSelect: true },
    { labelKey: 'confirm_label', valueKey: 'confirm_value', isSelect: true }
  ],
  bookings: [
    { labelKey: 'service_label', valueKey: 'service_value', isSelect: true },
    { labelKey: 'date_label', valueKey: 'date_value' },
    { labelKey: 'time_label', valueKey: 'time_value', isSelect: true },
    { labelKey: 'professional_label', valueKey: 'professional_value', isSelect: true }
  ],
  satisfaction_survey: [
    { labelKey: 'rating_label', valueKey: 'rating_value', isSelect: true },
    { labelKey: 'recommend_label', valueKey: 'recommend_value', isSelect: true },
    { labelKey: 'comment_label', valueKey: 'comment_value' },
    { labelKey: 'improve_label', valueKey: 'improve_value', isSelect: true }
  ],
  event_registration: [
    { labelKey: 'name_label', valueKey: 'name_value' },
    { labelKey: 'email_label', valueKey: 'email_value' },
    { labelKey: 'attendees_label', valueKey: 'attendees_value', isSelect: true },
    { labelKey: 'schedule_label', valueKey: 'schedule_value', isSelect: true }
  ],
  support_ticket: [
    { labelKey: 'topic_label', valueKey: 'topic_value', isSelect: true },
    { labelKey: 'description_label', valueKey: 'description_value' },
    { labelKey: 'urgency_label', valueKey: 'urgency_value', isSelect: true },
    { labelKey: 'order_ref_label', valueKey: 'order_ref_value' }
  ]
};

const ONBOARDING_PHONE_PROPS = {
  widthClassName: 'tw-w-[340px]',
  chatHeightClassName: 'tw-h-[440px]'
};

const StepIndicator = ({ steps, currentStep, t }) => (
  <ol className="tw-mt-6 tw-space-y-0">
    {steps.map((stepKey, index) => {
      const isActive = index === currentStep;
      const isCompleted = index < currentStep;

      return (
        <li key={stepKey} className="tw-flex tw-gap-3">
          <div className="tw-flex tw-flex-col tw-items-center">
            <div
              className={[
                'tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-sm tw-font-semibold',
                isActive && 'tw-bg-[#25d366] tw-text-white',
                isCompleted && 'tw-bg-[#25d366]/15 tw-text-[#128c7e]',
                !isActive && !isCompleted && 'tw-border tw-border-gray-300 tw-bg-white tw-text-gray-400'
              ].filter(Boolean).join(' ')}
            >
              {isCompleted ? (
                <IconMC name="checkmark-outline" size="sm" className="tw-text-[#128c7e]" />
              ) : (
                index + 1
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                className={[
                  'tw-my-1 tw-h-8 tw-w-px',
                  isCompleted ? 'tw-bg-[#25d366]' : 'tw-bg-gray-200'
                ].join(' ')}
              />
            )}
          </div>
          <div className="tw-pb-6 tw-pt-1">
            <p
              className={[
                'tw-text-sm',
                isActive ? 'tw-font-semibold tw-text-gray-900' : 'tw-text-gray-500'
              ].join(' ')}
            >
              {t(`onboarding.steps.${stepKey}.sidebar_label`)}
            </p>
          </div>
        </li>
      );
    })}
  </ol>
);

const buildUseCaseFields = (useCaseKey, t) => {
  const fieldKeys = USE_CASE_FIELD_KEYS[useCaseKey] || [];
  const basePath = `landing.use_cases.items.${useCaseKey}.fields`;

  return fieldKeys.map(({ labelKey, valueKey, isSelect }) => ({
    label: t(`${basePath}.${labelKey}`),
    value: t(`${basePath}.${valueKey}`),
    ...(isSelect ? { isSelect: true } : {})
  }));
};

const buildUseCaseMockupProps = (useCaseKey, t) => {
  const basePath = `landing.use_cases.items.${useCaseKey}`;

  return {
    contactName: t(`${basePath}.contact_name`),
    companyMessage: t(`${basePath}.company_message`),
    ctaLabel: t(`${basePath}.cta_label`),
    formTitle: t(`${basePath}.form_title`),
    submitLabel: t(`${basePath}.submit_label`),
    fields: buildUseCaseFields(useCaseKey, t),
    onlineLabel: t('landing.mockup.online'),
    ...ONBOARDING_PHONE_PROPS
  };
};

const WhatsappFlowsOnboardingModalMC = ({ visible, onClose, onFinish }) => {
  const { t } = useTranslation('WhatsappFlowsScreenMC');
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedUseCase, setSelectedUseCase] = useState(USE_CASES[0].key);

  const connectionRequirement = getWhatsappFlowsConnectionRequirement(retailer_info);

  const handleContactSupport = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setSelectedUseCase(USE_CASES[0].key);
      WhatsappFlowModelMC.trackEvent('onboarding_viewed').catch(() => {});
    }
  }, [visible]);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;
  const stepKey = STEPS[currentStep];

  const selectedUseCaseMockup = useMemo(
    () => buildUseCaseMockupProps(selectedUseCase, t),
    [selectedUseCase, t]
  );

  const handleNext = () => {
    if (isLastStep) {
      WhatsappFlowModelMC.trackEvent('onboarding_completed').catch(() => {});
      onFinish();
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const renderStepContent = () => {
    switch (stepKey) {
      case 'what_is':
        return (
          <div className="tw-flex tw-flex-col tw-gap-6 lg:tw-flex-row lg:tw-items-center">
            <WhatsappPhoneMockupMC {...buildUseCaseMockupProps('lead_qualification', t)} />
            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3">
              {['no_code', 'crm_saved'].map((benefitKey) => (
                <div
                  key={benefitKey}
                  className="tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4"
                >
                  <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-[#25d366]/15">
                    <IconMC
                      name={benefitKey === 'no_code' ? 'layers-outline' : 'file-text-outline'}
                      size="lg"
                      className="tw-text-[#128c7e]"
                    />
                  </div>
                  <div>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                      {t(`onboarding.steps.what_is.benefits.${benefitKey}.title`)}
                    </p>
                    <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">
                      {t(`onboarding.steps.what_is.benefits.${benefitKey}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'how_it_works':
        return (
          <div className="tw-space-y-4">
            {FLOW_STEPS.map((step, index) => (
              <div key={step.key} className="tw-flex tw-items-start tw-gap-4 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
                <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#1877f2] tw-text-sm tw-font-semibold tw-text-white">
                  {index + 1}
                </div>
                <div>
                  <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-gray-900">
                    <IconMC name={step.icon} className="tw-text-[#25d366]" />
                    {t(`landing.how_it_works.steps.${step.key}.title`)}
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">
                    {t(`landing.how_it_works.steps.${step.key}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        );
      case 'use_cases':
        return (
          <div className="tw-flex tw-gap-6">
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2 tw-overflow-y-auto tw-pr-1">
              {USE_CASES.map((useCase) => {
                const isSelected = selectedUseCase === useCase.key;

                return (
                  <button
                    key={useCase.key}
                    type="button"
                    onClick={() => setSelectedUseCase(useCase.key)}
                    className={[
                      'tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-p-3 tw-text-left tw-transition-colors',
                      isSelected
                        ? 'tw-border-[#25d366] tw-bg-[#25d366]/5'
                        : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300 hover:tw-bg-gray-50'
                    ].join(' ')}
                  >
                    <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-100">
                      <IconMC name={useCase.icon} size="lg" className={useCase.iconClassName} />
                    </div>
                    <div className="tw-min-w-0">
                      <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                        {t(`landing.use_cases.items.${useCase.key}.title`)}
                      </p>
                      <p className="tw-mt-1 tw-text-xs tw-leading-5 tw-text-gray-600">
                        {t(`landing.use_cases.items.${useCase.key}.description`)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="tw-shrink-0">
              <WhatsappPhoneMockupMC {...selectedUseCaseMockup} />
            </div>
          </div>
        );
      case 'requirements':
        return (
          <div className="tw-space-y-4">
            <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
              <IconMC name="checkmark-circle-2-outline" className="tw-mt-0.5 tw-shrink-0 tw-text-[#25d366]" />
              <div>
                <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                  {t('onboarding.steps.requirements.items.meta_flows.title')}
                </p>
                <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">
                  {t('onboarding.steps.requirements.items.meta_flows.description')}
                </p>
              </div>
            </div>

            {connectionRequirement === 'ready' && (
              <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-[#25d366]/30 tw-bg-[#25d366]/5 tw-p-4">
                <IconMC name="checkmark-circle-2-outline" className="tw-mt-0.5 tw-shrink-0 tw-text-[#25d366]" />
                <div>
                  <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                    {t('onboarding.steps.requirements.items.connection_ready.title')}
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-600">
                    {t('onboarding.steps.requirements.items.connection_ready.description')}
                  </p>
                </div>
              </div>
            )}

            {connectionRequirement === 'upgrade' && (
              <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
                <div className="tw-flex tw-items-start tw-gap-3">
                  <IconMC name="alert-circle-outline" className="tw-mt-0.5 tw-shrink-0 tw-text-amber-600" size="lg" />
                  <div>
                    <p className="tw-text-sm tw-font-semibold tw-text-amber-900">
                      {t('landing.bsp_alert.title')}
                    </p>
                    <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-amber-800">
                      {t('landing.bsp_alert.description')}
                    </p>
                  </div>
                </div>
                <ButtonMC
                  text={t('landing.bsp_alert.cta')}
                  onClick={handleContactSupport}
                  icon="message-circle-outline"
                  iconPosition="left"
                  size="small"
                  variant="SECONDARY"
                  className="tw-shrink-0 !tw-rounded-lg !tw-border-amber-300 !tw-bg-white !tw-text-amber-900 hover:!tw-bg-amber-100"
                  iconClassName="!tw-text-amber-700"
                />
              </div>
            )}

            {connectionRequirement === 'missing' && (
              <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-blue-200 tw-bg-blue-50 tw-p-4">
                <IconMC name="info-outline" className="tw-mt-0.5 tw-shrink-0 tw-text-blue-600" size="lg" />
                <div>
                  <p className="tw-text-sm tw-font-semibold tw-text-blue-900">
                    {t('onboarding.steps.requirements.items.connection_missing.title')}
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-blue-800">
                    {t('onboarding.steps.requirements.items.connection_missing.description')}
                  </p>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => window.open(META_FLOWS_DOCS_URL, '_blank', 'noopener,noreferrer')}
              className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-[#128c7e] hover:tw-underline"
            >
              {t('onboarding.steps.requirements.meta_docs_link')}
              <IconMC name="external-link-outline" size="sm" />
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <ModalMC
      visible={visible}
      onClose={onClose}
      closeOnOverlayClick={false}
      containerClassName="!tw-max-w-[1000px] tw-w-[90vw] !tw-p-0 tw-overflow-hidden"
      className="!tw-p-0"
      customHeader={(
        <div className="tw-flex tw-h-[640px] tw-w-full">
          <aside className="tw-flex tw-w-[240px] tw-shrink-0 tw-flex-col tw-border-r tw-border-gray-200 tw-bg-gray-50 tw-px-6 tw-py-6">
            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#25d366]/15">
                <IconMC name="flash-outline" className="tw-text-[#128c7e]" />
              </div>
              <p className="tw-text-sm tw-font-semibold tw-text-gray-900">{t('title')}</p>
            </div>
            <StepIndicator steps={STEPS} currentStep={currentStep} t={t} />
          </aside>

          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
            <div className="tw-flex-1 tw-overflow-y-auto tw-px-8 tw-py-8">
              <h2 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-gray-900">
                {t(`onboarding.steps.${stepKey}.title`)}
              </h2>
              <p className="tw-mt-2 tw-text-sm tw-leading-6 tw-text-gray-600">
                {t(`onboarding.steps.${stepKey}.description`)}
              </p>
              <div className="tw-mt-6">
                {renderStepContent()}
              </div>
            </div>

            <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-gray-200 tw-px-8 tw-py-5">
              {!isFirstStep && (
                <ButtonMC
                  text={t('onboarding.back')}
                  onClick={handleBack}
                  variant="SECONDARY"
                  className="!tw-rounded-lg"
                />
              )}
              <ButtonMC
                text={isLastStep ? t('onboarding.finish_cta') : t('onboarding.next')}
                onClick={handleNext}
                icon={isLastStep ? 'sync-outline' : 'arrow-forward-outline'}
                iconPosition="right"
                className="!tw-rounded-lg !tw-border-0 !tw-bg-[#25d366] hover:!tw-bg-[#20bd5a]"
              />
            </div>
          </div>
        </div>
      )}
      hideCloseButton={false}
    />
  );
};

export default WhatsappFlowsOnboardingModalMC;
